import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Capacitor } from '@capacitor/core';
import { Share2, MessageCircle, Link as LinkIcon } from 'lucide-react';
import Card from '../ui/Card.jsx';
import Button, { buttonClasses } from '../ui/Button.jsx';
import { toast } from '../notifications';
import {
  buildSessionShareMessage,
  buildSessionShareText,
  buildSessionShareUrl,
  buildWhatsAppUrl,
  canShareSession,
  trackSessionShared,
} from '../../utils/sessionShare';

/**
 * „Teilen“ für die eigene offene Session (Nov 2026, Codebuch § 3).
 *
 * Mit Web-Share-API (Telefon-Browser, iOS-App) öffnet „Teilen“ das Teilen-Menü
 * des Geräts. Ohne (Desktop-Browser, Android-App: der WebView kennt
 * navigator.share nicht) klappt „Teilen“ zwei Wege auf: „Per WhatsApp teilen“
 * (wa.me) und „Link kopieren“. Kein @capacitor/share, wie beim Logbuch-Link.
 *
 * Gezählt wird nur ein abgeschlossener Schritt (SESSION_SHARED): Teilen-Menü
 * bestätigt, WhatsApp-Link geöffnet, Link kopiert. Abbrechen zählt nicht.
 */
const ShareSessionCard = ({ session, currentUserId }) => {
  const [showFallback, setShowFallback] = useState(false);

  if (!canShareSession(session, currentUserId)) return null;

  const url = buildSessionShareUrl(session.id, { isNative: Capacitor.isNativePlatform() });
  const message = buildSessionShareMessage(session);
  const fullText = buildSessionShareText(session, url);

  const handleShare = async () => {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: session.topic || 'EachOtter-Session', text: message, url });
        trackSessionShared(session.id, 'native');
        return;
      } catch (error) {
        // Abbruch im Teilen-Menü ist kein Fehler; alles andere → die zwei Wege
        if (error?.name === 'AbortError') return;
      }
    }
    setShowFallback(true);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link kopiert');
      trackSessionShared(session.id, 'copy');
    } catch {
      toast.error('Kopieren nicht möglich. Bitte den Link markieren und kopieren.');
    }
  };

  return (
    <Card className="p-4 sm:p-[18px]">
      <h3 className="text-title-s font-semibold text-foreground">Mitlernende einladen</h3>
      <p className="mt-0.5 text-meta text-muted-foreground">
        Schick den Link in deine Klassengruppe. Wer ihn öffnet, meldet sich an und kann sich einen Platz sichern.
      </p>
      <Button
        variant="primary"
        onClick={handleShare}
        className="mt-3"
        fullWidth
        aria-expanded={showFallback}
      >
        <Share2 className="h-4 w-4" aria-hidden="true" />
        Teilen
      </Button>

      {showFallback && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="share-fallback">
          <a
            href={buildWhatsAppUrl(fullText)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackSessionShared(session.id, 'whatsapp')}
            className={buttonClasses('secondary', 'md', '', true)}
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            Per WhatsApp teilen
          </a>
          <Button variant="secondary" onClick={handleCopy} fullWidth>
            <LinkIcon className="h-4 w-4" aria-hidden="true" />
            Link kopieren
          </Button>
        </div>
      )}
    </Card>
  );
};

ShareSessionCard.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string,
    topic: PropTypes.string,
    hostId: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.string,
    isFocusMode: PropTypes.bool,
    maxParticipants: PropTypes.number,
    startTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    participants: PropTypes.arrayOf(PropTypes.object),
  }),
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ShareSessionCard;
